/**
 * Built-in glyph templates, authored as parametric stroke paths.
 *
 * Coordinates are in "line units": y = 0 is the top of a digit, y = 1 the
 * baseline, x grows to the right (screen convention, y down). Keeping every
 * glyph in the same frame means the synthesiser (synth.ts) can lay them out
 * as real text — a "-" floats at mid-height, a "." sits on the baseline —
 * while the recogniser normalises position and scale away.
 *
 * Several variants per character cover the common ways South African
 * learners write them (open/closed 4, 7 with a bar, 1 with a flag and base…).
 * Stroke order/direction variants aren't needed: $P ignores both.
 */
import type { HwChar } from '../../contracts.ts';
import type { Pt } from './geometry.ts';

export type Seg =
  | { k: 'M'; p: Pt }
  | { k: 'L'; p: Pt }
  | { k: 'Q'; c: Pt; p: Pt }
  | { k: 'C'; c1: Pt; c2: Pt; p: Pt }
  /** Elliptical arc; angles in degrees, 0 = right, 90 = down. Increasing angle runs clockwise on screen. */
  | { k: 'A'; c: Pt; rx: number; ry: number; from: number; to: number };

export type PathStroke = readonly Seg[];

export interface GlyphTemplate {
  char: HwChar;
  /** Variant name, e.g. "4-open-2" (char, style, stroke count). */
  name: string;
  strokes: readonly PathStroke[];
}

export const HW_CHARS: readonly HwChar[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '/', '.'];

export const isHwChar = (s: string): s is HwChar => (HW_CHARS as readonly string[]).includes(s);

// ───────────── Path DSL ─────────────

const P = (x: number, y: number): Pt => ({ x, y });
const M = (x: number, y: number): Seg => ({ k: 'M', p: P(x, y) });
const L = (x: number, y: number): Seg => ({ k: 'L', p: P(x, y) });
const Q = (cx: number, cy: number, x: number, y: number): Seg => ({ k: 'Q', c: P(cx, cy), p: P(x, y) });
const C = (x1: number, y1: number, x2: number, y2: number, x: number, y: number): Seg => ({
  k: 'C',
  c1: P(x1, y1),
  c2: P(x2, y2),
  p: P(x, y),
});
const A = (cx: number, cy: number, rx: number, ry: number, from: number, to: number): Seg => ({
  k: 'A',
  c: P(cx, cy),
  rx,
  ry,
  from,
  to,
});
const glyph = (char: HwChar, name: string, ...strokes: Seg[][]): GlyphTemplate => ({ char, name, strokes });

// ───────────── Templates ─────────────

export const BUILTIN_TEMPLATES: readonly GlyphTemplate[] = [
  // 0 — ellipse, both directions, with a little overshoot where the pen closes the loop.
  glyph('0', '0-ccw', [A(0.3, 0.5, 0.29, 0.5, -90, -460)]),
  glyph('0', '0-cw', [A(0.3, 0.5, 0.29, 0.5, -90, 280)]),
  glyph('0', '0-narrow', [A(0.24, 0.5, 0.22, 0.5, -60, -425)]),

  // 1 — plain stick, flagged, flagged with a base.
  glyph('1', '1-line', [M(0.05, 0), L(0.05, 1)]),
  glyph('1', '1-flag', [M(0, 0.28), L(0.2, 0), L(0.2, 1)]),
  glyph('1', '1-flag-base', [M(0.02, 0.26), L(0.24, 0), L(0.24, 1)], [M(0, 1), L(0.48, 1)]),

  // 2 — round top into a flat base; with a curl at the bottom; straight diagonal.
  glyph('2', '2-plain', [M(0.05, 0.26), C(0.08, -0.04, 0.6, -0.06, 0.58, 0.28), Q(0.55, 0.5, 0.02, 1), L(0.64, 1)]),
  glyph('2', '2-loop', [
    M(0.05, 0.26),
    C(0.08, -0.04, 0.6, -0.06, 0.58, 0.28),
    Q(0.52, 0.58, 0.16, 0.88),
    C(0.02, 1.02, -0.02, 0.8, 0.12, 0.86),
    C(0.24, 0.92, 0.34, 1, 0.64, 0.98),
  ]),
  glyph('2', '2-straight', [M(0.04, 0.3), C(0.04, 0, 0.6, -0.05, 0.56, 0.3), L(0.02, 1), L(0.62, 1)]),

  // 3 — two round bumps; flat "Z" top.
  glyph('3', '3-round', [
    M(0.05, 0.14),
    C(0.22, -0.05, 0.6, 0, 0.55, 0.24),
    C(0.5, 0.42, 0.3, 0.47, 0.2, 0.47),
    C(0.62, 0.47, 0.7, 0.78, 0.5, 0.93),
    C(0.35, 1.03, 0.1, 1, 0.02, 0.87),
  ]),
  glyph('3', '3-flat', [
    M(0.04, 0.02),
    L(0.58, 0.02),
    L(0.24, 0.42),
    C(0.66, 0.4, 0.7, 0.82, 0.45, 0.96),
    C(0.3, 1.03, 0.1, 0.99, 0.02, 0.87),
  ]),

  // 4 — open and closed (triangular), in one or two strokes.
  glyph('4', '4-open-2', [M(0.1, 0), L(0.03, 0.62), L(0.7, 0.62)], [M(0.5, 0.08), L(0.5, 1)]),
  glyph('4', '4-closed-2', [M(0.48, 0), L(0, 0.64), L(0.7, 0.64)], [M(0.48, 0), L(0.48, 1)]),
  glyph('4', '4-closed-1', [M(0.48, 1), L(0.48, 0), L(0, 0.64), L(0.72, 0.64)]),
  glyph('4', '4-open-1', [M(0.08, 0), L(0.03, 0.6), L(0.5, 0.6), L(0.5, 0.02), L(0.5, 1)]),

  // 5 — separate top bar, or bar-first in one stroke.
  glyph(
    '5',
    '5-two',
    [M(0.1, 0.02), L(0.07, 0.46), C(0.42, 0.3, 0.72, 0.56, 0.56, 0.84), C(0.42, 1.03, 0.12, 1, 0.02, 0.88)],
    [M(0.1, 0.02), L(0.6, 0.02)],
  ),
  glyph('5', '5-one', [
    M(0.6, 0.02),
    L(0.1, 0.02),
    L(0.07, 0.46),
    C(0.42, 0.3, 0.72, 0.56, 0.56, 0.84),
    C(0.42, 1.03, 0.12, 1, 0.02, 0.88),
  ]),

  // 6 — curved stem into a bottom loop; straighter stem with a round loop.
  glyph('6', '6-round', [
    M(0.52, 0.04),
    C(0.22, 0.1, 0.02, 0.4, 0.03, 0.7),
    C(0.04, 1.02, 0.56, 1.04, 0.56, 0.72),
    C(0.56, 0.44, 0.14, 0.44, 0.05, 0.68),
  ]),
  glyph('6', '6-straight', [M(0.46, 0), Q(0.06, 0.32, 0.05, 0.72), A(0.3, 0.73, 0.25, 0.26, 180, -175)]),

  // 7 — plain, with a serif, with a middle bar (two strokes), with a curved stem.
  glyph('7', '7-plain', [M(0.02, 0.04), L(0.62, 0.02), L(0.2, 1)]),
  glyph('7', '7-serif', [M(0.02, 0.18), L(0.03, 0.03), L(0.62, 0.03), L(0.22, 1)]),
  glyph('7', '7-bar', [M(0.02, 0.03), L(0.62, 0.03), L(0.22, 1)], [M(0.18, 0.52), L(0.6, 0.5)]),
  glyph('7', '7-curved', [M(0.02, 0.04), L(0.62, 0.03), Q(0.3, 0.45, 0.26, 1)]),

  // 8 — single S-stroke, two stacked loops (two strokes), "snowman" loops in one stroke.
  glyph('8', '8-single', [
    M(0.52, 0.15),
    C(0.46, -0.04, 0.08, -0.03, 0.1, 0.2),
    C(0.12, 0.38, 0.56, 0.46, 0.58, 0.73),
    C(0.6, 1.01, 0.04, 1.03, 0.05, 0.75),
    C(0.05, 0.55, 0.5, 0.4, 0.52, 0.15),
  ]),
  glyph('8', '8-loops', [A(0.3, 0.24, 0.21, 0.24, -90, 270)], [A(0.3, 0.72, 0.27, 0.28, -90, -450)]),
  glyph('8', '8-snowman', [A(0.3, 0.24, 0.21, 0.24, 90, -270), A(0.3, 0.72, 0.27, 0.28, -90, 270)]),

  // 9 — loop with a straight tail, or a curved tail.
  glyph('9', '9-straight', [A(0.3, 0.26, 0.26, 0.25, 0, -360), L(0.56, 1)]),
  glyph('9', '9-curved', [A(0.3, 0.26, 0.26, 0.25, 0, -360), C(0.56, 0.6, 0.5, 0.96, 0.18, 1)]),

  // Minus, fraction slash, decimal point.
  glyph('-', '-', [M(0, 0.5), L(0.5, 0.5)]),
  glyph('-', '-curved', [M(0, 0.52), Q(0.25, 0.47, 0.5, 0.52)]),
  glyph('/', '/-32', [M(0.62, 0), L(0, 1)]),
  glyph('/', '/-42', [M(0.9, 0), L(0, 1)]),
  glyph('.', '.-dot', [A(0.04, 0.93, 0.035, 0.035, 0, 360)]),
  glyph('.', '.-tap', [M(0.03, 0.93), L(0.035, 0.935)]),
];

// ───────────── Flattening ─────────────

const bezier2 = (a: Pt, c: Pt, b: Pt, t: number): Pt => {
  const u = 1 - t;
  return { x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y };
};

const bezier3 = (a: Pt, c1: Pt, c2: Pt, b: Pt, t: number): Pt => {
  const u = 1 - t;
  const w0 = u * u * u;
  const w1 = 3 * u * u * t;
  const w2 = 3 * u * t * t;
  const w3 = t * t * t;
  return {
    x: w0 * a.x + w1 * c1.x + w2 * c2.x + w3 * b.x,
    y: w0 * a.y + w1 * c1.y + w2 * c2.y + w3 * b.y,
  };
};

const arcPoint = (s: Extract<Seg, { k: 'A' }>, deg: number): Pt => {
  const r = (deg * Math.PI) / 180;
  return { x: s.c.x + s.rx * Math.cos(r), y: s.c.y + s.ry * Math.sin(r) };
};

/**
 * Turn one path stroke into a dense polyline (~`step` line units between
 * points on straight parts). An arc that doesn't start at the current pen
 * position is joined with a straight line, like a real pen would.
 */
export function flattenStroke(stroke: PathStroke, step = 0.02): Pt[] {
  const out: Pt[] = [];
  const lineTo = (p: Pt) => {
    const last = out[out.length - 1];
    if (!last) {
      out.push(p);
      return;
    }
    const n = Math.max(1, Math.ceil(Math.hypot(p.x - last.x, p.y - last.y) / step));
    for (let i = 1; i <= n; i++) out.push({ x: last.x + ((p.x - last.x) * i) / n, y: last.y + ((p.y - last.y) * i) / n });
  };
  for (const s of stroke) {
    const from = out[out.length - 1];
    switch (s.k) {
      case 'M':
        if (out.length) lineTo(s.p);
        else out.push(s.p);
        break;
      case 'L':
        lineTo(s.p);
        break;
      case 'Q':
      case 'C': {
        const a = from ?? (s.k === 'Q' ? s.c : s.c1);
        const n = 24;
        for (let i = from ? 1 : 0; i <= n; i++) {
          const t = i / n;
          out.push(s.k === 'Q' ? bezier2(a, s.c, s.p, t) : bezier3(a, s.c1, s.c2, s.p, t));
        }
        break;
      }
      case 'A': {
        const n = Math.max(2, Math.ceil(Math.abs(s.to - s.from) / 7.5));
        lineTo(arcPoint(s, s.from));
        for (let i = 1; i <= n; i++) out.push(arcPoint(s, s.from + ((s.to - s.from) * i) / n));
        break;
      }
    }
  }
  return out;
}

export const flattenGlyph = (g: GlyphTemplate, step?: number): Pt[][] => g.strokes.map((s) => flattenStroke(s, step));
